import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  IconButton
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

/**
 * Derive display name for a file (API may return documentManagerId populated or raw).
 */
function getFileDisplayName(file) {
  const dm = file.documentManagerId;
  if (dm && typeof dm === 'object') {
    return dm.name ?? dm.documentName ?? dm.title ?? 'File';
  }
  if (dm) return `Document ${String(dm)}`;
  return `File ${String(file._id ?? file.id ?? '').slice(0, 8)}`;
}

/**
 * Get current user's access level for a file (when viewing shared-with-me list).
 * Uses effectiveAccessLevel from API when present (e.g. inside shared folder), else direct accessControl.
 * @returns 'view' | 'edit' | null
 */
function getSharedWithMeAccessLevel(file, currentUserEmployeeId) {
  if (file.effectiveAccessLevel) return file.effectiveAccessLevel;
  if (!currentUserEmployeeId || !file.accessControl) return null;
  const ac = file.accessControl.find(
    (a) => String(a.employeeId) === String(currentUserEmployeeId)
  );
  return ac?.accessLevel ?? null;
}

/**
 * List of files in the current location.
 * Supports actions: view/details, manage access, delete.
 * When sharedWithMeMode is true: no manage access; delete only if user has edit access.
 */
export default function FileList({
  files = [],
  loading,
  onViewFile,
  onManageAccess,
  onDeleteFile,
  sharedWithMeMode = false,
  currentUserEmployeeId = ''
}) {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 120,
          py: 2
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!files || files.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No files
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {files.map((file) => {
        const id = file._id ?? file.id;
        const name = getFileDisplayName(file);
        const docId =
          file.documentManagerId && typeof file.documentManagerId !== 'object'
            ? String(file.documentManagerId)
            : String(file.documentManagerId?._id ?? '');
        const canDelete =
          !sharedWithMeMode ||
          getSharedWithMeAccessLevel(file, currentUserEmployeeId) === 'edit';
        const showManageAccess = !sharedWithMeMode;
        return (
          <ListItem
            key={id}
            disablePadding
            sx={{ mb: 0.5, borderRadius: 1 }}
            secondaryAction={
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewFile?.(file);
                  }}
                  aria-label="Details"
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
                {showManageAccess && (
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageAccess?.(file);
                    }}
                    aria-label="Manage access"
                  >
                    <PeopleAltOutlinedIcon fontSize="small" />
                  </IconButton>
                )}
                {canDelete && (
                  <IconButton
                    edge="end"
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteFile?.(file);
                    }}
                    aria-label="Delete"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            }
          >
            <ListItemButton
              onClick={() => onViewFile?.(file)}
              sx={{ borderRadius: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <DescriptionIcon color="action" />
              </ListItemIcon>
              <ListItemText
                primary={name}
                secondary={docId ? `Document ID: ${docId}` : undefined}
              />
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );
}
